import { ChangeDetectionStrategy, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { BalooService } from '@fi-app/external-platform-integration/baloo/baloo.service';
import { BehaviorSubject } from 'rxjs';
import { IBalooSyncLog } from '@fi-app/external-platform-integration/baloo/baloo';
import { AppTextService } from '@fi-shared/app-text';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { ObjectTypes } from '@fi-shared/types';
import { getObjectTypeNameTextId } from '@fi-shared/utils';
import { BalooSyncTabIndex } from '@fi-app/external-platform-integration/baloo/baloo-sync-log-summary-items.enum';
import { FiSpinnerService } from '@fi-shared/fi-spinner/fi-spinner.service';
import { ProgressType } from '@fi-shared/fi-signal-r/fi-signal-enums';

interface IManualSyncTab {
    tabLabel: string;
    headerLabel: string;
    descriptionLabel: string;
    testSyncButtonClick: () => void;
    syncButtonClick: () => void;
    syncButtonLabel: string;
    downloadLog: () => void;
}

@Component({
    selector: 'fi-baloo-manual-sync',
    templateUrl: './baloo-manual-sync.component.html',
    styleUrls: ['./baloo-manual-sync.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-baloo-manual-sync' },
    standalone: false,
})
export class BalooManualSyncComponent implements OnInit {
    manualSyncTabs: IManualSyncTab[] = [];
    selectedTabIndex$: BehaviorSubject<number> = new BehaviorSubject<number>(BalooSyncTabIndex.Unit);
    isUnitTestSync: boolean = false;
    isRoleTestSync: boolean = false;
    isUserTestSync: boolean = false;

    syncLogs: { [key: number]: BehaviorSubject<IBalooSyncLog> } = {
        [BalooSyncTabIndex.Unit]: new BehaviorSubject<IBalooSyncLog>(undefined),
        [BalooSyncTabIndex.Role]: new BehaviorSubject<IBalooSyncLog>(undefined),
        [BalooSyncTabIndex.User]: new BehaviorSubject<IBalooSyncLog>(undefined),
    };

    constructor(
        private balooService: BalooService,
        private appTextService: AppTextService,
        private fiCustomerDataService: FiCustomerDataService,
        private fiSpinnerService: FiSpinnerService,
    ) {}

    ngOnInit() {
        this.setManualSyncTabs();
    }

    getSyncInProgressTextByTabIndex(): string {
        switch (this.selectedTabIndex$.value) {
            case BalooSyncTabIndex.Unit:
                return this.appTextService.get(
                    this.isUnitTestSync
                        ? 'baloo.manual_sync_in_progress_test_unit'
                        : 'baloo.manual_sync_in_progress_unit',
                );
            case BalooSyncTabIndex.Role:
                return this.appTextService.get(
                    this.isUnitTestSync
                        ? 'baloo.manual_sync_in_progress_test_role'
                        : 'baloo.manual_sync_in_progress_role',
                );
            case BalooSyncTabIndex.User:
                return this.appTextService.get(
                    this.isUnitTestSync
                        ? 'baloo.manual_sync_in_progress_test_user'
                        : 'baloo.manual_sync_in_progress_user',
                );
        }
    }

    getSyncLogHeading(): string {
        switch (this.selectedTabIndex$.value) {
            case BalooSyncTabIndex.Unit:
                return this.appTextService.get(
                    this.isUnitTestSync ? 'baloo.unit_sync_manual_test' : 'baloo.unit_sync_manual',
                );
            case BalooSyncTabIndex.Role:
                return this.appTextService.get(
                    this.isRoleTestSync ? 'baloo.role_sync_manual_test' : 'baloo.role_sync_manual',
                );
            case BalooSyncTabIndex.User:
                return this.appTextService.get(
                    this.isUserTestSync ? 'baloo.user_sync_manual_test' : 'baloo.user_sync_manual',
                );
        }
    }

    private setManualSyncTabs() {
        this.manualSyncTabs = [
            {
                tabLabel: this.appTextService.get('common.units'),
                headerLabel: this.getLabelWithCustomerDisplayName('baloo.manual_sync_units_tab_header'),
                descriptionLabel: this.getLabelWithCustomerDisplayName('baloo.manual_sync_units_tab_description'),
                testSyncButtonClick: () => this.syncUnits(true),
                syncButtonClick: () => this.syncUnits(),
                syncButtonLabel: this.appTextService.get('baloo.sync_units'),
                downloadLog: () => {},
            },
            {
                tabLabel: this.appTextService.get('common.roles'),
                headerLabel: this.getLabelWithCustomerDisplayName('baloo.manual_sync_roles_tab_header'),
                descriptionLabel: this.getLabelWithCustomerDisplayName('baloo.manual_sync_roles_tab_description'),
                testSyncButtonClick: () => this.syncRoles(true),
                syncButtonClick: () => this.syncRoles(),
                syncButtonLabel: this.appTextService.get('baloo.sync_roles'),
                downloadLog: () => {},
            },
            {
                tabLabel: this.appTextService.get('common.users'),
                headerLabel: this.getLabelWithCustomerDisplayName('baloo.manual_sync_users_tab_header'),
                descriptionLabel: this.getLabelWithCustomerDisplayName('baloo.manual_sync_users_tab_description'),
                testSyncButtonClick: () => this.syncUsers(true),
                syncButtonClick: () => this.syncUsers(),
                syncButtonLabel: this.appTextService.get('baloo.sync_users'),
                downloadLog: () => {},
            },
        ];
    }

    private getLabelWithCustomerDisplayName(textId: string): string {
        return this.appTextService.get(textId, { '{0}': this.fiCustomerDataService.customerData.DisplayName });
    }

    syncUnits(isTestMode?: boolean) {
        this.isUnitTestSync = isTestMode;
        const promise = this.balooService.syncUnits(isTestMode).then((syncLog: IBalooSyncLog) => {
            this.syncLogs[BalooSyncTabIndex.Unit].next(syncLog);
        });
        this.showProgress(promise, ObjectTypes.BusinessUnit, isTestMode);
    }

    syncRoles(isTestMode?: boolean) {
        this.isRoleTestSync = isTestMode;
        const promise = this.balooService.syncRoles(isTestMode).then((syncLog: IBalooSyncLog) => {
            this.syncLogs[BalooSyncTabIndex.Role].next(syncLog);
        });
        this.showProgress(promise, ObjectTypes.Role, isTestMode);
    }

    syncUsers(isTestMode?: boolean) {
        this.isUserTestSync = isTestMode;
        const promise = this.balooService.syncUsers(isTestMode).then((syncLog: IBalooSyncLog) => {
            this.syncLogs[BalooSyncTabIndex.User].next(syncLog);
        });
        this.showProgress(promise, ObjectTypes.User, isTestMode);
    }

    private showProgress(promise: Promise<void>, objectType: ObjectTypes, isTestMode: boolean) {
        let headerTextId: string;
        if (isTestMode) {
            headerTextId = 'baloo.testing_sync';
        } else if (objectType === ObjectTypes.User) {
            headerTextId = 'baloo.synchronizing_users';
        } else if (objectType === ObjectTypes.BusinessUnit) {
            headerTextId = 'baloo.synchronizing_units';
        } else if (objectType === ObjectTypes.Role) {
            headerTextId = 'baloo.synchronizing_roles';
        } else {
            throw new Error(`Unknown object type ${objectType} in showProgress`);
        }
        return this.fiSpinnerService.trackPromise(promise, {
            headerText: this.appTextService.get(headerTextId),
            signalRProgressType:
                objectType === ObjectTypes.User ? ProgressType.BalooSyncUsers : ProgressType.BalooSyncGroups,
        });
    }

    selectedTabIndexChange(newTabIndex: number) {
        this.selectedTabIndex$.next(newTabIndex);
    }

    protected readonly ObjectTypes = ObjectTypes;
    protected readonly BalooSyncTabIndex = BalooSyncTabIndex;
}
